/**
 * ConfigurarPipelinePanel — editor global do pipeline (SlideOverPanel).
 *
 * Markup fiel ao mockup:
 *   - banner info "Alteracoes ... aplicadas globalmente ... N negocios remapeados"
 *   - label "ETAPAS · N ATIVAS"
 *   - lista de .pipe-cfg-row: handle (arrastar via @dnd-kit) + cor (.color-pick)
 *     + nome (.nm input) + select tipo + .count "N negocios" + editar/excluir
 *   - botao largura total "+ Adicionar etapa"
 *   - secao "REGRAS GLOBAIS" com 3 checkboxes (labels exatos)
 *
 * Grupos de colunas (Fase D): toggle "Usar grupos de colunas". Ligado, abre a
 * secao de gestao de grupos (nome + cor + reordenar + remover) e exige que
 * TODAS as colunas ativas pertencam a um grupo (regra ALL-OR-NOTHING do
 * backend). Cada etapa referencia o grupo por `grupo_key` (id real OU `_key`
 * estavel do grupo novo) — o backend casa por essa chave (salvar_pipeline).
 *
 * Coluna de entrada (CRM-37): cada linha tem o seletor "Entrada" — exatamente
 * uma marcada, nunca a de conversao (FINALIZADO). Excluir a entrada pede a
 * nova antes de sair da lista; sem isso o salvar recusava com o nome do campo
 * e a unica saida era cancelar. Regras puras em ./pipelineConfig.
 *
 * Regras de dias (CRM-46): aviso de leads parados e arquivamento de perdidos
 * sao numeros editaveis (0 desliga). O aviso mostra para quem vai
 * (`pipeline.destinatarios_aviso`: usuarios ativos com pipeline:configurar).
 *
 * Salvar -> configurarPipeline({etapas, grupos, regras, remapeamentos}) no
 * caller (CrmBoard), que recarrega o board. Reordenacao via @dnd-kit/sortable.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FiInfo, FiMove, FiPlus, FiRotateCcw, FiTrash2 } from "react-icons/fi";

import SlideOverPanel from "../../components/SlideOverPanel";
import { TIPOS_ETAPA } from "../../services/crm";
import {
  LIMITE_DIAS_REGRA_PIPELINE,
  ROTULO_ARQUIVAR_PERDIDOS,
  ROTULO_AVISO_PARADOS,
  TIPO_CONVERSAO,
  avisoFormulariosDaColuna,
  candidatasEntrada,
  destinosDeRemapeamento,
  ehColunaDeConversao,
  exigeConfirmacaoRemocao,
  formulariosDaEtapa,
  marcarComoEntrada,
  removerEtapa as removerEtapaDaLista,
  rotuloFormulariosDaEtapa,
  validarDiasRegra,
  validarEntrada,
} from "./pipelineConfig";

// Cor default ao criar etapa nova (cinza neutro do seed).
const COR_PADRAO = "#64748B";

// ---------- Degrade de cores derivadas do grupo ----------
// Quando grupos estao ligados, cada coluna recebe um tom da cor do seu grupo:
// degrade do mais claro (1a coluna) ate a cor cheia do grupo (ultima = "mais
// forte"), variando so a luminosidade (matiz/saturacao do grupo preservados).

function _hexToHsl(hex) {
  let h = String(hex || "").trim().replace(/^#/, "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return null;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let s = 0;
  let hue = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) hue = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) hue = (b - r) / d + 2;
    else hue = (r - g) / d + 4;
    hue /= 6;
  }
  return { h: hue, s, l };
}

function _hslToHex(h, s, l) {
  let r;
  let g;
  let b;
  if (s === 0) {
    r = l;
    g = l;
    b = l;
  } else {
    const hue2rgb = (p, q, t) => {
      let tt = t;
      if (tt < 0) tt += 1;
      if (tt > 1) tt -= 1;
      if (tt < 1 / 6) return p + (q - p) * 6 * tt;
      if (tt < 1 / 2) return q;
      if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  const toHex = (x) => {
    const v = Math.max(0, Math.min(255, Math.round(x * 255)));
    return v.toString(16).padStart(2, "0");
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// Cor da coluna i (0-based) de um grupo com n colunas. i=n-1 retorna a cor
// EXATA do grupo (deterministico, sem round-trip de hsl) = tom mais forte.
export function tonDoGrupo(corGrupo, i, n) {
  if (n <= 1) return corGrupo;
  if (i >= n - 1) return corGrupo;
  const hsl = _hexToHsl(corGrupo);
  if (!hsl) return corGrupo;
  const lLight = hsl.l + (1 - hsl.l) * 0.62; // ponto mais claro (sempre > l)
  const t = i / (n - 1); // 0 (1a) .. 1 (ultima)
  const lAtual = lLight + (hsl.l - lLight) * t; // claro -> luminosidade do grupo
  return _hslToHex(hsl.h, hsl.s, lAtual);
}

// Recalcula a cor das colunas agrupadas (degrade por grupo, na ordem do board),
// preservando colunas com override manual (_corManual). Retorna a MESMA
// referencia quando nada muda (corta o loop do useEffect que a chama).
export function aplicarCoresAutomaticas(etapas, grupos) {
  const corPorKey = new Map((grupos || []).map((g) => [g._key, g.cor || COR_PADRAO]));
  const indicesPorGrupo = new Map();
  etapas.forEach((e, idx) => {
    const k = e.grupo_key || "";
    if (!corPorKey.has(k)) return;
    if (!indicesPorGrupo.has(k)) indicesPorGrupo.set(k, []);
    indicesPorGrupo.get(k).push(idx);
  });
  const novas = new Map();
  indicesPorGrupo.forEach((indices, k) => {
    const corG = corPorKey.get(k);
    indices.forEach((idx, i) => {
      if (etapas[idx]._corManual) return;
      novas.set(idx, tonDoGrupo(corG, i, indices.length));
    });
  });
  let mudou = false;
  const next = etapas.map((e, idx) => {
    if (novas.has(idx) && novas.get(idx) !== e.cor) {
      mudou = true;
      return { ...e, cor: novas.get(idx) };
    }
    return e;
  });
  return mudou ? next : etapas;
}

// Na hidratacao, reconstroi _corManual: se a cor salva difere do tom que o
// degrade produziria, foi override manual (preserva entre reaberturas, sem
// precisar de campo no backend).
function marcarManuais(etapas, grupos) {
  const derivadas = aplicarCoresAutomaticas(
    etapas.map((e) => ({ ...e, _corManual: false })),
    grupos
  );
  const temGrupo = new Set((grupos || []).map((g) => g._key));
  return etapas.map((e, idx) => {
    if (!temGrupo.has(e.grupo_key || "")) return e;
    return derivadas[idx].cor !== e.cor ? { ...e, _corManual: true } : e;
  });
}

let _tempSeq = 0;
function novaEtapaTemp(ordem) {
  _tempSeq += 1;
  return {
    _key: `tmp-${_tempSeq}`,
    id: undefined, // sem id -> backend cria
    nome: "",
    cor: COR_PADRAO,
    ordem,
    tipo: "EM_ANDAMENTO",
    is_default_entrada: false,
    ativo: true,
    leads_count: 0,
    grupo_key: "", // ref do grupo (backend casa por grupo_key)
  };
}

let _tempSeqGrupo = 0;
function novoGrupoTemp(ordem) {
  _tempSeqGrupo += 1;
  return {
    _key: `grp-tmp-${_tempSeqGrupo}`,
    id: undefined, // sem id -> backend cria
    nome: "",
    cor: COR_PADRAO,
    ordem,
  };
}

// Garante um _key estavel para o dnd-kit (id real OU chave temporaria).
function comKeys(etapas) {
  return etapas.map((e, idx) => ({
    ...e,
    _key: e._key || (e.id != null ? String(e.id) : `tmp-existing-${idx}`),
    // grupo_key referencia o grupo: usa o que veio do backend (`grupo`=id)
    // ou string vazia (sem grupo). O backend casa por grupo_key (id ou _key).
    grupo_key:
      e.grupo_key != null
        ? String(e.grupo_key)
        : e.grupo != null
        ? String(e.grupo)
        : "",
  }));
}

// Garante um _key estavel para cada grupo (id real OU chave temporaria).
function gruposComKeys(grupos) {
  return (grupos || []).map((g, idx) => ({
    ...g,
    _key: g._key || (g.id != null ? String(g.id) : `grp-existing-${idx}`),
  }));
}

// ---------- Linha sortable ----------

function PipeRow({
  etapa,
  onMudar,
  onMarcarEntrada,
  onExcluir,
  podeExcluir,
  usarGrupos,
  grupos,
  formularios,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: etapa._key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { opacity: 0.6, zIndex: 2 } : null),
  };

  const count = etapa.leads_count ?? 0;
  const rotuloFormularios = rotuloFormulariosDaEtapa(formularios);
  // A coluna de conversao nao recebe leads novos: lead que nasce nela nao
  // vira ganho (o backend recusa em salvar_pipeline e em criar_lead).
  const conversao = ehColunaDeConversao(etapa);
  // Quando o grupo escolhido sumiu da lista (removido), o select cai em "".
  const grupoSelecionado = grupos.some(
    (g) => g._key === (etapa.grupo_key || "")
  )
    ? etapa.grupo_key || ""
    : "";

  return (
    <div className="pipe-cfg-row" ref={setNodeRef} style={style}>
      <button
        type="button"
        className="drag"
        aria-label={`Reordenar etapa ${etapa.nome || "sem nome"}`}
        style={{ background: "transparent", border: 0, cursor: "grab", padding: 0 }}
        {...attributes}
        {...listeners}
      >
        <FiMove size={14} aria-hidden="true" />
      </button>

      <input
        type="color"
        className="color-pick"
        value={etapa.cor || COR_PADRAO}
        onChange={(e) =>
          onMudar(
            usarGrupos
              ? { cor: e.target.value, _corManual: true }
              : { cor: e.target.value }
          )
        }
        title={
          usarGrupos
            ? "Cor automática do grupo — clique para sobrescrever manualmente"
            : "Cor da etapa"
        }
        aria-label={`Cor da etapa ${etapa.nome || "sem nome"}`}
      />

      {usarGrupos && etapa._corManual && (
        <button
          type="button"
          className="btn btn--ghost btn--icon"
          onClick={() => onMudar({ _corManual: false })}
          title="Voltar à cor automática do grupo"
          aria-label={`Resetar cor da etapa ${etapa.nome || "sem nome"} para a cor do grupo`}
          style={{ flex: "0 0 auto" }}
        >
          <FiRotateCcw size={13} aria-hidden="true" />
        </button>
      )}

      <div className="nm">
        <input
          type="text"
          value={etapa.nome}
          onChange={(e) => onMudar({ nome: e.target.value })}
          placeholder="Nome da etapa"
          maxLength={60}
          aria-label="Nome da etapa"
        />
      </div>

      <select
        value={etapa.tipo}
        onChange={(e) => onMudar({ tipo: e.target.value })}
        aria-label="Tipo da etapa"
        style={{
          fontSize: "0.78rem",
          padding: "4px 6px",
          borderRadius: 6,
          border: "1px solid var(--border)",
          background: "var(--surface)",
          color: "var(--ink)",
        }}
      >
        {TIPOS_ETAPA.map((t) => (
          <option
            key={t.value}
            value={t.value}
            // Virar coluna de conversao sendo a entrada deixaria o pipeline
            // num estado que o backend recusa: troque a entrada antes.
            disabled={t.value === TIPO_CONVERSAO && etapa.is_default_entrada}
          >
            {t.label}
          </option>
        ))}
      </select>

      <label
        className="entrada"
        title={
          conversao
            ? "A coluna de conversão não pode receber os leads novos"
            : "Coluna que recebe os leads novos (manuais, de formulário e remapeados)"
        }
      >
        <input
          type="radio"
          name="pipeline-coluna-de-entrada"
          checked={Boolean(etapa.is_default_entrada)}
          disabled={conversao}
          onChange={onMarcarEntrada}
          aria-label={`Leads novos entram em ${etapa.nome || "(sem nome)"}`}
        />
        Entrada
      </label>

      {usarGrupos && (
        <select
          value={grupoSelecionado}
          onChange={(e) => onMudar({ grupo_key: e.target.value })}
          aria-label={`Grupo da etapa ${etapa.nome || "sem nome"}`}
          style={{
            fontSize: "0.78rem",
            padding: "4px 6px",
            borderRadius: 6,
            border:
              "1px solid " +
              (grupoSelecionado ? "var(--border)" : "var(--danger)"),
            background: "var(--surface)",
            color: "var(--ink)",
            maxWidth: 130,
          }}
        >
          <option value="">Sem grupo…</option>
          {grupos.map((g) => (
            <option key={g._key} value={g._key}>
              {g.nome || "(grupo sem nome)"}
            </option>
          ))}
        </select>
      )}

      {/* Quem mais depende da coluna: formulario do site que manda leads para
          ca. Sem isso, a coluna saia e a captacao seguia apontando para o
          vazio, sem ninguem notar (FORM-01). */}
      <div
        className="count"
        title={
          rotuloFormularios
            ? `${count} negocio(s) nesta etapa · ${rotuloFormularios} do site mandam leads para ca`
            : `${count} negocio(s) nesta etapa`
        }
      >
        {count} negocio{count === 1 ? "" : "s"}
        {rotuloFormularios && (
          <span
            style={{ display: "block", fontSize: "0.72rem", color: "var(--ink-3)" }}
          >
            {rotuloFormularios}
          </span>
        )}
      </div>

      <div className="actions">
        <button
          type="button"
          className="btn btn--ghost btn--icon"
          onClick={onExcluir}
          disabled={!podeExcluir}
          title={
            podeExcluir
              ? "Excluir etapa"
              : "Não é possível excluir a única etapa restante"
          }
          aria-label={`Excluir etapa ${etapa.nome || "sem nome"}`}
        >
          <FiTrash2 size={13} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

// ---------- Linha de grupo (sortable) ----------

function GrupoRow({ grupo, onMudar, onExcluir }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: grupo._key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { opacity: 0.6, zIndex: 2 } : null),
  };

  return (
    <div className="pipe-cfg-row" ref={setNodeRef} style={style}>
      <button
        type="button"
        className="drag"
        aria-label={`Reordenar grupo ${grupo.nome || "sem nome"}`}
        style={{ background: "transparent", border: 0, cursor: "grab", padding: 0 }}
        {...attributes}
        {...listeners}
      >
        <FiMove size={14} aria-hidden="true" />
      </button>

      <input
        type="color"
        className="color-pick"
        value={grupo.cor || COR_PADRAO}
        onChange={(e) => onMudar({ cor: e.target.value })}
        aria-label={`Cor do grupo ${grupo.nome || "sem nome"}`}
      />

      <div className="nm">
        <input
          type="text"
          value={grupo.nome}
          onChange={(e) => onMudar({ nome: e.target.value })}
          placeholder="Nome do grupo"
          maxLength={60}
          aria-label="Nome do grupo"
        />
      </div>

      <div className="actions">
        <button
          type="button"
          className="btn btn--ghost btn--icon"
          onClick={onExcluir}
          title="Excluir grupo"
          aria-label={`Excluir grupo ${grupo.nome || "sem nome"}`}
        >
          <FiTrash2 size={13} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

// ---------- Regra com prazo em dias ----------

/**
 * Checkbox (liga/desliga) + numero de dias editavel. 0 desliga, como no
 * backend. Os dias eram fixos no codigo: um 30 salvo no banco aparecia como
 * "14 dias" e voltava para 14 no proximo salvamento (CRM-46).
 *
 * O valor fica como texto enquanto a pessoa digita e so e normalizado no blur
 * — normalizar a cada tecla reescreve o que ela esta escrevendo.
 */
function RegraDias({ id, rotulo, valor, padrao, onChange, antes, depois, children }) {
  const ligada = String(valor ?? "").trim() !== "0";
  const [ultimoLigado, setUltimoLigado] = useState(ligada ? valor : padrao);

  useEffect(() => {
    const texto = String(valor ?? "").trim();
    if (texto !== "" && texto !== "0") setUltimoLigado(valor);
  }, [valor]);

  const alternar = (event) => {
    onChange(event.target.checked ? String(ultimoLigado ?? padrao) : "0");
  };

  const normalizar = () => {
    const resultado = validarDiasRegra(valor, rotulo);
    if (!resultado.erro) onChange(String(resultado.dias));
  };

  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: "0.88rem" }}>
      <input
        type="checkbox"
        id={id}
        checked={ligada}
        onChange={alternar}
        style={{ marginTop: 5 }}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <label htmlFor={id} style={{ cursor: "pointer" }}>
            {antes}
          </label>
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={LIMITE_DIAS_REGRA_PIPELINE}
            step={1}
            value={ligada ? valor : ultimoLigado}
            disabled={!ligada}
            onChange={(e) => onChange(e.target.value)}
            onBlur={normalizar}
            aria-label={`${rotulo}: dias`}
            style={{
              width: 72,
              padding: "4px 6px",
              borderRadius: 6,
              border: "1px solid var(--border)",
              background: "var(--surface)",
              color: "var(--ink)",
              font: "inherit",
              fontSize: "0.85rem",
            }}
          />
          <span>{depois}</span>
        </span>
        {ligada && children}
      </div>
    </div>
  );
}

// ---------- Painel ----------

function ConfigurarPipelinePanel({
  open,
  onClose,
  pipeline, // {etapas:[...], grupos:[...], regras:{...}}
  // {etapaId: {total, formularios:[nome]}} — GET /api/formularios/etapas-em-uso
  formulariosPorEtapa = {},
  onSalvar, // async ({etapas, grupos, regras, remapeamentos}) => leads_remapeados
  salvando = false,
}) {
  const [etapas, setEtapas] = useState([]);
  const [regras, setRegras] = useState({
    pipeline_permite_vendedor_mover: true,
    pipeline_notificar_gerente_dias: 14,
    pipeline_auto_arquivar_perdidos_dias: 90,
    pipeline_largura_coluna: "MEDIA",
  });
  const [erro, setErro] = useState("");
  // REF2-3: ao excluir uma etapa com negocios, o usuario escolhe o destino.
  // remapeamentos = {etapaId: destinoId}; confirmRemocao = {etapa, destino} | null.
  const [remapeamentos, setRemapeamentos] = useState({});
  const [confirmRemocao, setConfirmRemocao] = useState(null);
  // Grupos de colunas (Fase D). usarGrupos OFF = todas etapas sem grupo;
  // ON = regra ALL-OR-NOTHING (toda etapa ativa precisa de grupo).
  const [grupos, setGrupos] = useState([]);
  const [usarGrupos, setUsarGrupos] = useState(false);

  // Hidrata ao abrir / quando o pipeline muda.
  useEffect(() => {
    if (!open) return;
    const etapasHidratadas = comKeys(pipeline?.etapas || []);
    const gruposHidratados = gruposComKeys(pipeline?.grupos || []);
    const usarG = gruposHidratados.length > 0;
    // Reconstroi _corManual a partir das cores salvas (override preservado).
    setEtapas(
      usarG ? marcarManuais(etapasHidratadas, gruposHidratados) : etapasHidratadas
    );
    setGrupos(gruposHidratados);
    // Liga o modo "usar grupos" quando ja existem grupos salvos.
    setUsarGrupos(usarG);
    setRegras({
      pipeline_permite_vendedor_mover:
        pipeline?.regras?.pipeline_permite_vendedor_mover ?? true,
      pipeline_notificar_gerente_dias:
        pipeline?.regras?.pipeline_notificar_gerente_dias ?? 14,
      pipeline_auto_arquivar_perdidos_dias:
        pipeline?.regras?.pipeline_auto_arquivar_perdidos_dias ?? 90,
      pipeline_largura_coluna:
        pipeline?.regras?.pipeline_largura_coluna ?? "MEDIA",
    });
    setErro("");
    setRemapeamentos({});
    setConfirmRemocao(null);
  }, [open, pipeline]);

  // Degrade automatico: com grupos ligados, cada coluna recebe um tom da cor
  // do seu grupo (claro -> forte na ordem do board), reaplicado quando muda a
  // cor do grupo, a atribuicao das colunas ou a ordem. Colunas com override
  // manual (_corManual) sao preservadas. aplicarCoresAutomaticas devolve a
  // mesma referencia quando nada muda, entao o efeito estabiliza (sem loop).
  useEffect(() => {
    if (!usarGrupos) return;
    setEtapas((prev) => aplicarCoresAutomaticas(prev, grupos));
  }, [usarGrupos, grupos, etapas]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const ativas = useMemo(
    () => etapas.filter((e) => e.ativo !== false).length,
    [etapas]
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setEtapas((prev) => {
      const oldIndex = prev.findIndex((e) => e._key === active.id);
      const newIndex = prev.findIndex((e) => e._key === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const mudarEtapa = (key, patch) => {
    setEtapas((prev) =>
      prev.map((e) => {
        if (e._key !== key) return e;
        const next = { ...e, ...patch };
        // Mudar a coluna de grupo re-deriva a cor (limpa o override manual).
        if ("grupo_key" in patch) next._corManual = false;
        return next;
      })
    );
  };

  // Uma unica coluna recebe os leads novos.
  const marcarEntrada = (key) => {
    setEtapas((prev) => marcarComoEntrada(prev, key));
  };

  // Excluir etapa: com negocios, pergunta para onde eles vao; sendo a coluna
  // de entrada, pergunta quem passa a receber os leads novos — antes o erro
  // so aparecia ao salvar, com o nome do campo, e nao havia como corrigir.
  const pedirExclusao = (etapa) => {
    // Coluna que algum formulario usa como destino nunca sai calada: o
    // remapeamento acontece de qualquer jeito, e o gestor precisa saber para
    // onde os leads do site passam a ir (FORM-01).
    const formularios = formulariosDaEtapa(formulariosPorEtapa, etapa);
    if (!exigeConfirmacaoRemocao(etapa, formularios.total)) {
      setEtapas((prev) => removerEtapaDaLista(prev, etapa._key));
      return;
    }
    const destinos = destinosDeRemapeamento(etapas, etapa._key);
    const candidatas = candidatasEntrada(etapas, etapa._key);
    setConfirmRemocao({
      etapa,
      formularios,
      destino: destinos[0]?.id != null ? String(destinos[0].id) : "",
      novaEntrada: candidatas[0]?._key || "",
    });
  };

  const confirmarRemocao = () => {
    const etapa = confirmRemocao?.etapa;
    if (!etapa) return;
    if (etapa.id != null && confirmRemocao.destino) {
      setRemapeamentos((prev) => ({ ...prev, [etapa.id]: confirmRemocao.destino }));
    }
    setEtapas((prev) =>
      removerEtapaDaLista(
        prev,
        etapa._key,
        etapa.is_default_entrada ? confirmRemocao.novaEntrada : undefined
      )
    );
    setConfirmRemocao(null);
  };

  const adicionarEtapa = () => {
    setEtapas((prev) => [...prev, novaEtapaTemp(prev.length)]);
  };

  // ---------- Grupos ----------

  const handleDragEndGrupos = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setGrupos((prev) => {
      const oldIndex = prev.findIndex((g) => g._key === active.id);
      const newIndex = prev.findIndex((g) => g._key === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const mudarGrupo = (key, patch) => {
    setGrupos((prev) => prev.map((g) => (g._key === key ? { ...g, ...patch } : g)));
  };

  const removerGrupo = (key) => {
    setGrupos((prev) => prev.filter((g) => g._key !== key));
    // Limpa a referencia das etapas que apontavam para o grupo removido.
    setEtapas((prev) =>
      prev.map((e) => (e.grupo_key === key ? { ...e, grupo_key: "" } : e))
    );
  };

  const adicionarGrupo = () => {
    setGrupos((prev) => [...prev, novoGrupoTemp(prev.length)]);
  };

  // Liga/desliga o uso de grupos. OFF limpa a referencia de grupo das etapas.
  const alternarUsarGrupos = (ligado) => {
    setUsarGrupos(ligado);
    setErro("");
    if (!ligado) {
      // Desligar limpa o vinculo de grupo e o override manual (re-deriva limpo
      // quando religar). As cores atuais ficam como estao no modo flat.
      setEtapas((prev) =>
        prev.map((e) => ({ ...e, grupo_key: "", _corManual: false }))
      );
    }
  };

  const handleSalvar = async () => {
    setErro("");
    // Validacao minima: toda etapa precisa de nome.
    const semNome = etapas.some((e) => !String(e.nome || "").trim());
    if (semNome) {
      setErro("Todas as etapas precisam de um nome.");
      return;
    }
    if (etapas.length === 0) {
      setErro("O pipeline precisa de ao menos uma etapa.");
      return;
    }

    // As mesmas recusas do servidor (salvar_pipeline), ditas antes de mandar.
    const erroEntrada = validarEntrada(etapas);
    if (erroEntrada) {
      setErro(erroEntrada);
      return;
    }
    const aviso = validarDiasRegra(
      regras.pipeline_notificar_gerente_dias,
      ROTULO_AVISO_PARADOS
    );
    if (aviso.erro) {
      setErro(aviso.erro);
      return;
    }
    const arquivamento = validarDiasRegra(
      regras.pipeline_auto_arquivar_perdidos_dias,
      ROTULO_ARQUIVAR_PERDIDOS
    );
    if (arquivamento.erro) {
      setErro(arquivamento.erro);
      return;
    }

    // Grupos so vao no payload quando o modo esta ligado.
    let payloadGrupos = [];
    if (usarGrupos) {
      // Todo grupo precisa de nome.
      const grupoSemNome = grupos.some((g) => !String(g.nome || "").trim());
      if (grupoSemNome) {
        setErro("Todos os grupos precisam de um nome.");
        return;
      }
      if (grupos.length === 0) {
        setErro(
          "Crie ao menos um grupo ou desligue \"Usar grupos de colunas\"."
        );
        return;
      }
      // Conjunto de _keys validos para validar a referencia das etapas.
      const keysValidos = new Set(grupos.map((g) => g._key));
      // Regra ALL-OR-NOTHING: toda etapa ativa precisa apontar para um grupo.
      const ativasSemGrupo = etapas.filter(
        (e) => e.ativo !== false && !keysValidos.has(e.grupo_key || "")
      );
      if (ativasSemGrupo.length > 0) {
        const nomes = ativasSemGrupo
          .map((e) => `"${String(e.nome || "").trim() || "(sem nome)"}"`)
          .join(", ");
        setErro(
          `Com grupos ligados, TODAS as colunas precisam de um grupo. ` +
            `Faltando: ${nomes}.`
        );
        return;
      }
      payloadGrupos = grupos.map((g, idx) => ({
        ...(g.id != null ? { id: g.id } : {}),
        _key: g._key, // chave estavel: o backend casa etapa.grupo_key por aqui
        nome: String(g.nome).trim(),
        cor: g.cor || COR_PADRAO,
        ordem: idx,
      }));
    }

    // Reaplica `ordem` pela posicao atual; envia so os campos do contrato.
    const payloadEtapas = etapas.map((e, idx) => ({
      ...(e.id != null ? { id: e.id } : {}),
      nome: String(e.nome).trim(),
      cor: e.cor || COR_PADRAO,
      ordem: idx,
      tipo: e.tipo || "EM_ANDAMENTO",
      is_default_entrada: Boolean(e.is_default_entrada),
      ativo: e.ativo !== false,
      // grupo_key referencia o grupo (id real OU _key temporario); "" quando
      // o modo esta desligado. O backend ignora grupo_key vazio (sem grupo).
      grupo_key: usarGrupos ? e.grupo_key || "" : "",
    }));
    try {
      await onSalvar({
        etapas: payloadEtapas,
        grupos: payloadGrupos,
        // Os dias sao texto enquanto a pessoa digita; vao validados e ja
        // convertidos, para "030" ou " 30 " nunca virarem erro do servidor.
        regras: {
          ...regras,
          pipeline_notificar_gerente_dias: aviso.dias,
          pipeline_auto_arquivar_perdidos_dias: arquivamento.dias,
        },
        remapeamentos,
      });
    } catch (err) {
      setErro(err?.message || "Erro ao salvar o pipeline.");
    }
  };

  const footer = (
    <>
      <button
        className="btn btn--secondary"
        type="button"
        onClick={onClose}
        disabled={salvando}
      >
        Cancelar
      </button>
      <button
        className="btn btn--primary"
        type="button"
        onClick={handleSalvar}
        disabled={salvando}
      >
        {salvando ? "Salvando..." : "Salvar pipeline"}
      </button>
    </>
  );

  // Modal de exclusao: para onde vao os leads e quem passa a receber os leads
  // novos quando a coluna que sai e a de entrada.
  const alvoRemocao = confirmRemocao?.etapa || null;
  const destinosRemocao = alvoRemocao
    ? destinosDeRemapeamento(etapas, alvoRemocao._key)
    : [];
  const candidatasDeEntrada = alvoRemocao
    ? candidatasEntrada(etapas, alvoRemocao._key)
    : [];
  const removendoEntrada = Boolean(alvoRemocao?.is_default_entrada);
  const leadsARemapear =
    alvoRemocao?.id != null ? alvoRemocao.leads_count ?? 0 : 0;
  const formulariosDaRemocao = confirmRemocao?.formularios || {
    total: 0,
    formularios: [],
  };
  // O destino escolhido vale para os leads E para os formularios: o servidor
  // reaponta os dois na mesma transacao.
  const precisaEscolherDestino = leadsARemapear > 0 || formulariosDaRemocao.total > 0;
  const nomeDoDestino =
    destinosRemocao.find((e) => String(e.id) === String(confirmRemocao?.destino))
      ?.nome || "";
  const avisoDosFormularios = avisoFormulariosDaColuna(
    formulariosDaRemocao,
    nomeDoDestino,
  );

  const ids = etapas.map((e) => e._key);

  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      title="Configurar pipeline"
      subtitle="Etapas e regras globais do CRM"
      width="min(96vw, 680px)"
      footer={footer}
    >
      <div className="so-banner info">
        <FiInfo aria-hidden="true" />
        <span>
          Alterações no pipeline são aplicadas globalmente e registradas em
          audit log. Ao excluir uma etapa que tenha negócios, você escolhe para
          qual coluna mover os leads.
        </span>
      </div>

      {erro && (
        <div className="so-banner danger">
          <FiInfo aria-hidden="true" />
          <span>{erro}</span>
        </div>
      )}

      {/* Modal centralizado (overlay) — sempre visivel, independente do scroll
          do painel. O backdrop fecha = Cancelar. */}
      {confirmRemocao && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Mover negocios antes de excluir a etapa"
          onClick={() => setConfirmRemocao(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1200,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            onClick={(ev) => ev.stopPropagation()}
            style={{
              width: "min(92%, 420px)",
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-xl, 14px)",
              boxShadow: "var(--shadow-3, 0 12px 40px rgba(0,0,0,0.3))",
              padding: 20,
              display: "flex",
              flexDirection: "column",
              gap: 14,
            }}
          >
            <div
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 600,
                fontSize: 15,
              }}
            >
              Excluir etapa &quot;{confirmRemocao.etapa.nome || "(sem nome)"}&quot;
            </div>
            {leadsARemapear > 0 && (
              <span style={{ fontSize: "0.9rem", color: "var(--ink-2)" }}>
                Esta etapa tem{" "}
                <strong>
                  {leadsARemapear} negócio
                  {leadsARemapear === 1 ? "" : "s"}
                </strong>
                . Para qual coluna mover esses leads antes de excluir?
              </span>
            )}
            {/* CRM-08: a contagem e a do board (so negocios abertos), mas o
                backend remapeia TODOS os leads da coluna. O rotulo diz a
                regua para ninguem ler "3" e o sistema mover dez. */}
            {alvoRemocao?.id != null && (
              <span style={{ fontSize: "0.8rem", color: "var(--ink-3)" }}>
                A contagem é dos negócios abertos do quadro: leads ganhos,
                perdidos e arquivados desta coluna também são movidos.
              </span>
            )}
            {avisoDosFormularios && (
              <div className="so-banner warn" role="status" style={{ margin: 0 }}>
                <FiInfo aria-hidden="true" />
                <span>{avisoDosFormularios}</span>
              </div>
            )}

            {precisaEscolherDestino &&
              (destinosRemocao.length > 0 ? (
                <label
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    fontSize: "0.78rem",
                    color: "var(--ink-3)",
                  }}
                >
                  {leadsARemapear > 0
                    ? "Mover leads para"
                    : "Apontar os formulários para"}
                  <select
                    value={confirmRemocao.destino}
                    onChange={(e) =>
                      setConfirmRemocao((c) => ({ ...c, destino: e.target.value }))
                    }
                    aria-label="Etapa de destino dos leads"
                    autoFocus
                    style={{
                      padding: "8px 10px",
                      borderRadius: 8,
                      border: "1px solid var(--border)",
                      background: "var(--surface)",
                      color: "var(--ink)",
                      fontSize: "0.9rem",
                    }}
                  >
                    {destinosRemocao.map((e) => (
                      <option key={e._key} value={String(e.id)}>
                        {e.nome || "(sem nome)"}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <span style={{ fontSize: "0.8rem", color: "var(--ink-3)" }}>
                  Não há outra coluna salva para receber esses leads — eles irão
                  para a coluna de entrada.
                </span>
              ))}

            {/* A coluna de entrada nao pode sair sem sucessora: sem isso o
                salvar recusava o pipeline inteiro e a saida era cancelar. */}
            {removendoEntrada &&
              (candidatasDeEntrada.length > 0 ? (
                <label
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    fontSize: "0.78rem",
                    color: "var(--ink-3)",
                  }}
                >
                  Esta é a coluna que recebe os leads novos. Quem passa a recebê-los?
                  <select
                    value={confirmRemocao.novaEntrada}
                    onChange={(e) =>
                      setConfirmRemocao((c) => ({ ...c, novaEntrada: e.target.value }))
                    }
                    aria-label="Nova coluna de entrada"
                    style={{
                      padding: "8px 10px",
                      borderRadius: 8,
                      border: "1px solid var(--border)",
                      background: "var(--surface)",
                      color: "var(--ink)",
                      fontSize: "0.9rem",
                    }}
                  >
                    {candidatasDeEntrada.map((e) => (
                      <option key={e._key} value={e._key}>
                        {e.nome || "(sem nome)"}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <span style={{ fontSize: "0.8rem", color: "var(--danger-text)" }}>
                  Nenhuma outra coluna pode receber os leads novos (a de conversão
                  não pode). Adicione uma coluna antes de excluir esta.
                </span>
              ))}
            <div
              style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}
            >
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => setConfirmRemocao(null)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={confirmarRemocao}
                disabled={removendoEntrada && !confirmRemocao.novaEntrada}
              >
                {leadsARemapear > 0 ? "Mover e excluir" : "Excluir"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toggle: usar grupos de colunas (Fase D). */}
      <label
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          fontSize: "0.88rem",
          cursor: "pointer",
          margin: "4px 0 14px",
        }}
      >
        <input
          type="checkbox"
          checked={usarGrupos}
          onChange={(e) => alternarUsarGrupos(e.target.checked)}
          style={{ marginTop: 2 }}
        />
        <span>
          Usar grupos de colunas
          <span
            style={{
              display: "block",
              fontSize: "0.78rem",
              color: "var(--ink-3)",
              marginTop: 2,
            }}
          >
            Quando ligado, TODAS as colunas precisam pertencer a um grupo.
          </span>
        </span>
      </label>

      {/* Grupos vem ANTES das etapas: define os grupos + cores e depois as
          colunas, que herdam o degrade da cor do seu grupo. */}
      {usarGrupos && (
        <>
          <div
            style={{
              fontSize: "0.72rem",
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "var(--ink-3)",
              margin: "4px 0 10px",
            }}
          >
            Grupos de colunas · {grupos.length}
          </div>

          {grupos.length === 0 ? (
            <div
              style={{
                fontSize: "0.85rem",
                color: "var(--ink-3)",
                padding: "10px 0",
              }}
            >
              Nenhum grupo ainda. Adicione um grupo e atribua as colunas a ele.
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEndGrupos}
            >
              <SortableContext
                items={grupos.map((g) => g._key)}
                strategy={verticalListSortingStrategy}
              >
                {grupos.map((grupo) => (
                  <GrupoRow
                    key={grupo._key}
                    grupo={grupo}
                    onMudar={(patch) => mudarGrupo(grupo._key, patch)}
                    onExcluir={() => removerGrupo(grupo._key)}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}

          <button
            type="button"
            className="btn btn--secondary"
            onClick={adicionarGrupo}
            style={{ width: "100%", justifyContent: "center", marginTop: 4 }}
          >
            <FiPlus size={14} aria-hidden="true" /> Adicionar grupo
          </button>
        </>
      )}

      <div
        style={{
          fontSize: "0.72rem",
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--ink-3)",
          margin: usarGrupos ? "20px 0 10px" : "4px 0 10px",
        }}
      >
        Etapas · {ativas} ativas
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {etapas.map((etapa) => (
            <PipeRow
              key={etapa._key}
              etapa={etapa}
              onMudar={(patch) => mudarEtapa(etapa._key, patch)}
              onMarcarEntrada={() => marcarEntrada(etapa._key)}
              onExcluir={() => pedirExclusao(etapa)}
              podeExcluir={etapas.length > 1}
              usarGrupos={usarGrupos}
              grupos={grupos}
              formularios={formulariosDaEtapa(formulariosPorEtapa, etapa)}
            />
          ))}
        </SortableContext>
      </DndContext>

      <button
        type="button"
        className="btn btn--secondary"
        onClick={adicionarEtapa}
        style={{ width: "100%", justifyContent: "center", marginTop: 4 }}
      >
        <FiPlus size={14} aria-hidden="true" /> Adicionar etapa
      </button>

      <div
        style={{
          fontSize: "0.72rem",
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--ink-3)",
          margin: "20px 0 10px",
        }}
      >
        Regras globais
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <label
          style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: "0.88rem", cursor: "pointer" }}
        >
          <input
            type="checkbox"
            checked={regras.pipeline_permite_vendedor_mover}
            onChange={(e) =>
              setRegras((r) => ({ ...r, pipeline_permite_vendedor_mover: e.target.checked }))
            }
            style={{ marginTop: 2 }}
          />
          <span>Permitir vendedores moverem cards entre colunas</span>
        </label>

        <RegraDias
          id="regra-aviso-leads-parados"
          rotulo={ROTULO_AVISO_PARADOS}
          valor={regras.pipeline_notificar_gerente_dias}
          padrao={14}
          onChange={(valor) =>
            setRegras((r) => ({ ...r, pipeline_notificar_gerente_dias: valor }))
          }
          antes={`${ROTULO_AVISO_PARADOS}: avisar quem configura o pipeline quando um lead fica mais de`}
          depois="dias na mesma etapa"
        >
          {/* O aviso ia para o reply-to da loja e, sem reply-to, nao ia para
              ninguem. Agora vai para quem configura o pipeline — e a tela diz
              quem e, ou avisa que ninguem recebe. */}
          {Array.isArray(pipeline?.destinatarios_aviso) &&
            (pipeline.destinatarios_aviso.length > 0 ? (
              <span style={{ fontSize: "0.78rem", color: "var(--ink-3)" }}>
                Será enviado para:{" "}
                {pipeline.destinatarios_aviso.map((d) => d.nome).join(", ")} — pelo
                e-mail configurado da unidade.
              </span>
            ) : (
              <div className="so-banner warn" role="status" style={{ margin: 0 }}>
                <FiInfo aria-hidden="true" />
                <span>
                  Ninguém recebe este aviso: nenhum usuário ativo desta unidade tem
                  permissão para configurar o pipeline.
                </span>
              </div>
            ))}
        </RegraDias>

        <RegraDias
          id="regra-arquivar-perdidos"
          rotulo={ROTULO_ARQUIVAR_PERDIDOS}
          valor={regras.pipeline_auto_arquivar_perdidos_dias}
          padrao={90}
          onChange={(valor) =>
            setRegras((r) => ({ ...r, pipeline_auto_arquivar_perdidos_dias: valor }))
          }
          antes={`${ROTULO_ARQUIVAR_PERDIDOS}: arquivar sozinho depois de`}
          depois="dias da perda"
        />

        <div>
          <div style={{ fontSize: "0.88rem", marginBottom: 8 }}>
            Largura das colunas do board
          </div>
          <div
            className="tabchip-group"
            role="group"
            aria-label="Largura das colunas do board"
          >
            {[
              { v: "PEQUENA", l: "Pequena" },
              { v: "MEDIA", l: "Média" },
              { v: "GRANDE", l: "Grande" },
            ].map((o) => {
              const ativo = (regras.pipeline_largura_coluna || "MEDIA") === o.v;
              return (
                <div
                  key={o.v}
                  role="button"
                  tabIndex={0}
                  aria-pressed={ativo}
                  className={"tabchip" + (ativo ? " active" : "")}
                  onClick={() =>
                    setRegras((r) => ({ ...r, pipeline_largura_coluna: o.v }))
                  }
                  onKeyDown={(e) =>
                    (e.key === "Enter" || e.key === " ") &&
                    setRegras((r) => ({ ...r, pipeline_largura_coluna: o.v }))
                  }
                >
                  {o.l}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </SlideOverPanel>
  );
}

export default ConfigurarPipelinePanel;
